/*----------------------------------
- SECTION: CONSENSUS CHART
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { getAppraisalProvider, type AppraisalProviderId } from '@uniquedomains/core/domains/appraisal';

import { INK_BLOCK_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import type { PRODUCT_APPRAISE_PAGE_CONTENT } from '@/client/catalogs/landing/product/appraise/pageContent';

/*----------------------------------
- TYPES
----------------------------------*/

type ConsensusChartProps = {
    content: (typeof PRODUCT_APPRAISE_PAGE_CONTENT)['consensus'];
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

// Illustrative figures for one invented example, not a catalog row. A public
// surface never shows real estimates, so this chart is drawn from constants and
// is labelled as a sample everywhere it appears.
//
// The plotted set is deliberately NOT the live provider set: the section exists to
// show what reading two independent opinions looks like, and GoDaddy is the second
// opinion the surrounding copy discusses. Availability is stated by the comparison
// table below and by the caption here, so the example never implies GoDaddy runs
// today. Identity still comes from the provider catalog, never from copy.
const SAMPLE_RANGE: { id: AppraisalProviderId; low: number; headline: number; high: number } = {
    id: 'humbleworth',
    low: 6_900,
    headline: 24_300,
    high: 41_800,
};
const SAMPLE_POINT: { id: AppraisalProviderId; headline: number } = { id: 'godaddy', headline: 17_400 };

/** The axis starts at zero, so every distance drawn is the distance between the figures. */
const AXIS_MAX = 45_000;

/** Where the two independent opinions meet: the range the chip quotes. */
const OVERLAP_LOW = Math.min(SAMPLE_POINT.headline, SAMPLE_RANGE.headline);
const OVERLAP_HIGH = Math.max(SAMPLE_POINT.headline, SAMPLE_RANGE.headline);

/*----------------------------------
- HELPERS
----------------------------------*/

const percent = (value: number): string => `${((value / AXIS_MAX) * 100).toFixed(1)}%`;
const formatThousands = (value: number): string => `$${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
const providerLabel = (id: AppraisalProviderId): string => getAppraisalProvider(id)?.label ?? id;

/** Above the axis: the four figures, with the two that bracket the overlap picked out. */
const TICKS = [
    { value: SAMPLE_RANGE.low, inRange: false },
    { value: OVERLAP_LOW, inRange: true },
    { value: OVERLAP_HIGH, inRange: true },
    { value: SAMPLE_RANGE.high, inRange: false },
];

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One name read through two sources on a shared axis: the range model draws a band
 * from its floor to its ceiling, the point estimate lands as a pin, and the stretch
 * where the two agree is filled solid. Inline spans rather than a chart library, and
 * one responsive tree: a marketing figure this small should cost no JavaScript.
 */
const ConsensusChart = ({ content }: ConsensusChartProps) => (
    <div data-reveal class={`${INK_BLOCK_CLASS} p-5 lg:p-8`}>
        <div class="flex items-center justify-between gap-2.5">
            <span class="text-[15px] font-bold lg:text-[18px]">
                {content.sampleName} <span class="font-medium text-white/50">· {content.sampleStatus}</span>
            </span>
            <span class="flex-none rounded-full bg-[#f2e97e] px-2.5 py-[5px] text-[11px] font-extrabold text-[#1b1d1e] lg:px-3 lg:py-1.5 lg:text-[12.5px]">
                <span class="hidden sm:inline">{content.rangeChipLabel} · </span>
                {formatThousands(OVERLAP_LOW)} to {formatThousands(OVERLAP_HIGH)}
            </span>
        </div>

        <figure class="m-0 mt-5 lg:mt-9">
            <figcaption class="sr-only">
                {providerLabel(SAMPLE_RANGE.id)} {formatThousands(SAMPLE_RANGE.low)} to{' '}
                {formatThousands(SAMPLE_RANGE.high)}, {providerLabel(SAMPLE_POINT.id)}{' '}
                {formatThousands(SAMPLE_POINT.headline)}. {content.illustrativeNote}
            </figcaption>

            <div aria-hidden="true">
                <div class="relative h-[15px] lg:h-[17px]">
                    {TICKS.map((tick) => (
                        <span
                            key={tick.value}
                            class={`absolute top-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-bold tabular-nums lg:text-[12px] ${tick.inRange ? 'text-[#7bc89a]' : 'text-white/60'}`}
                            style={{ left: percent(tick.value) }}
                        >
                            {formatThousands(tick.value)}
                        </span>
                    ))}
                </div>

                <div class="relative my-2 h-[10px] lg:my-3 lg:h-[14px]">
                    <span class="absolute inset-x-0 top-1/2 h-[2px] -translate-y-1/2 bg-white/15" />
                    <span
                        class="absolute inset-y-0 rounded-full bg-[#7bc89a]/30"
                        style={{ left: percent(SAMPLE_RANGE.low), width: percent(SAMPLE_RANGE.high - SAMPLE_RANGE.low) }}
                    />
                    <span
                        class="absolute inset-y-0 rounded-full bg-[#7bc89a]"
                        style={{ left: percent(OVERLAP_LOW), width: percent(OVERLAP_HIGH - OVERLAP_LOW) }}
                    />
                    <span
                        class="absolute top-1/2 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-[#141516] bg-white lg:h-[22px] lg:w-[22px] lg:border-4"
                        style={{ left: percent(SAMPLE_POINT.headline) }}
                    />
                </div>

                <div class="relative h-[15px] lg:h-[17px]">
                    <span
                        class="absolute top-0 -translate-x-1/2 whitespace-nowrap text-[10.5px] font-bold text-white/70 lg:text-[12px]"
                        style={{ left: percent(SAMPLE_POINT.headline) }}
                    >
                        {providerLabel(SAMPLE_POINT.id)}
                    </span>
                    {/* Pinned to the band's ceiling rather than its middle: centred, it
                        would sit on top of the point estimate's label on a phone. */}
                    <span
                        class="absolute top-0 -translate-x-full whitespace-nowrap text-[10.5px] font-bold text-[#7bc89a] lg:text-[12px]"
                        style={{ left: percent(SAMPLE_RANGE.high) }}
                    >
                        {providerLabel(SAMPLE_RANGE.id)}<span class="hidden sm:inline"> range</span>
                    </span>
                </div>
            </div>
        </figure>

        <p class="mt-3.5 text-[13.5px] leading-[1.55] text-white/70 lg:mt-5 lg:text-[14.5px] lg:leading-[1.6]">
            {content.cardBody}
        </p>
        <p class="mt-2.5 text-[11.5px] leading-[1.5] text-white/55 lg:text-[12px]">{content.illustrativeNote}</p>
    </div>
);

export default ConsensusChart;
